import { useState, useCallback, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/sonner";
import { Loader2, ArrowLeft, ArrowRight, Check, MessageSquare, Instagram, Send, Zap, Bot, Target, Megaphone, Columns3 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AutomationFunnelStageSelector } from "@/components/automations/nodes/AutomationFunnelStageSelector";
import { AutomationTagSelector } from "@/components/automations/nodes/AutomationTagSelector";
import {
  CrmWizardFields,
  crmActionNodeDefs,
  crmActionsReady,
  emptyCrmWizardActions,
  type CrmWizardActions,
} from "@/components/automations/CrmWizardFields";
import { buildTriggerConfig, maxExecutionsForTrigger } from "@/lib/automation-flow";
import {
  canRegisterAsCampaign,
  isCampaignChannel,
  parseKeywordList,
  triggerTypeSupportsCampaignKeywords,
} from "@/lib/automation-campaign";

/* ─── Channel definitions ─── */
const CHANNELS = [
  { value: "whatsapp", label: "WhatsApp", icon: MessageSquare, color: "text-green-500" },
  { value: "instagram_dm", label: "Instagram DM", icon: Instagram, color: "text-pink-500" },
  { value: "instagram_story", label: "Instagram Story", icon: Instagram, color: "text-purple-500" },
  { value: "instagram_comment", label: "Instagram Comentário", icon: Instagram, color: "text-orange-500" },
  { value: "messenger", label: "Messenger", icon: Send, color: "text-blue-500" },
  { value: "crm", label: "CRM", icon: Columns3, color: "text-primary" },
] as const;

/* ─── Triggers válidos por canal ─── */
const CHANNEL_TRIGGERS: Record<string, string[]> = {
  whatsapp: ["keyword", "first_message", "media_type"],
  instagram_dm: ["keyword", "first_message", "media_type"],
  instagram_story: ["story_reply", "keyword"],
  instagram_comment: ["comment", "keyword"],
  messenger: ["keyword", "first_message", "media_type"],
  crm: ["lead_stage_entered", "tag_added"],
};

const TRIGGER_TYPES = [
  { value: "keyword", label: "Palavra-chave", description: "Dispara quando o contato envia uma palavra específica" },
  { value: "first_message", label: "Primeira mensagem", description: "Dispara na primeira mensagem de um novo contato" },
  { value: "story_reply", label: "Resposta a Story", description: "Dispara quando alguém responde ao seu Story" },
  { value: "comment", label: "Comentário", description: "Dispara quando alguém comenta em um post" },
  { value: "media_type", label: "Tipo de mídia", description: "Dispara com base no tipo de mídia recebido" },
  { value: "lead_stage_entered", label: "Entrou na coluna", description: "Dispara quando o lead entra em uma etapa do funil" },
  { value: "tag_added", label: "Tag adicionada", description: "Dispara quando uma tag é aplicada no lead" },
];

const AI_CONTROL_MODES = [
  { value: "automation_only", label: "Apenas automação", description: "Só a automação responde, sem IA" },
  { value: "automation_then_ai", label: "Automação + IA", description: "Executa a automação, depois a IA assume" },
  { value: "continuous", label: "Modo contínuo", description: "A automação envia a primeira resposta; quando o contato responder, a IA assume" },
  { value: "fallback_ai", label: "IA como fallback", description: "Se a automação não casar, a IA responde normalmente" },
];

const TEMPLATES = [
  { id: "welcome", label: "Boas-vindas", description: "Mensagem automática para novos contatos", triggerType: "first_message", channel: null as string | null },
  { id: "story_engage", label: "Engajamento de Story", description: "Resposta automática a replies de Story", triggerType: "story_reply", channel: "instagram_story" },
  { id: "qualification", label: "Qualificação", description: "Perguntas de qualificação com palavras-chave", triggerType: "keyword", channel: null as string | null },
];

interface AutomationWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  onCreated: () => void;
}

export function AutomationWizard({ open, onOpenChange, workspaceId, onCreated }: AutomationWizardProps) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [channel, setChannel] = useState("");
  const [triggerType, setTriggerType] = useState("keyword");
  const [triggerKeywords, setTriggerKeywords] = useState("");
  const [triggerMediaType, setTriggerMediaType] = useState("image");
  const [responseMessage, setResponseMessage] = useState("");
  const [aiControlMode, setAiControlMode] = useState("automation_then_ai");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  /* ─── Lead destination (optional) ─── */
  const [moveLeadEnabled, setMoveLeadEnabled] = useState(false);
  const [destinationFunnelId, setDestinationFunnelId] = useState<string>("");
  const [destinationFunnelName, setDestinationFunnelName] = useState<string>("");
  const [destinationStageId, setDestinationStageId] = useState<string>("");
  const [destinationStageName, setDestinationStageName] = useState<string>("");
  const [destinationStageSlug, setDestinationStageSlug] = useState<string>("");

  /* ─── Campanha (WhatsApp / Instagram + palavras-chave) ─── */
  const [createAsCampaign, setCreateAsCampaign] = useState(false);
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [adSetName, setAdSetName] = useState("");
  const [adName, setAdName] = useState("");
  const [campaignNotes, setCampaignNotes] = useState("");
  const [crmActions, setCrmActions] = useState<CrmWizardActions>(emptyCrmWizardActions);
  const [triggerFunnelId, setTriggerFunnelId] = useState("");
  const [triggerFunnelName, setTriggerFunnelName] = useState("");
  const [triggerStageId, setTriggerStageId] = useState("");
  const [triggerStageName, setTriggerStageName] = useState("");
  const [triggerStageSlug, setTriggerStageSlug] = useState("");
  const [triggerTagId, setTriggerTagId] = useState("");
  const [triggerTagName, setTriggerTagName] = useState("");

  const parsedKeywords = useMemo(
    () => parseKeywordList(triggerKeywords),
    [triggerKeywords],
  );

  const showCampaignOption = useMemo(
    () =>
      isCampaignChannel(channel) &&
      triggerTypeSupportsCampaignKeywords(triggerType) &&
      parsedKeywords.length > 0,
    [channel, triggerType, parsedKeywords],
  );

  /* ─── Derived: does the selected channel have a fixed (single) trigger? ─── */
  const channelHasFixedTrigger = useMemo(() => {
    if (!channel) return false;
    const triggers = CHANNEL_TRIGGERS[channel] ?? [];
    return triggers.length === 1;
  }, [channel]);

  /* Logical steps: 0=basics, 1=trigger(skippable), 2=message, 3=ai, 4=destination, 5=review */
  const isCrmChannel = channel === "crm";

  const LOGICAL_STEPS = useMemo(() => {
    if (isCrmChannel) return [0, 1, 2, 5];
    if (channelHasFixedTrigger) return [0, 2, 3, 4, 5]; // skip step 1
    return [0, 1, 2, 3, 4, 5];
  }, [channelHasFixedTrigger, isCrmChannel]);

  const totalVisualSteps = LOGICAL_STEPS.length;
  const currentVisualIndex = LOGICAL_STEPS.indexOf(step);

  /* ─── Channel change handler ─── */
  const handleChannelChange = useCallback((newChannel: string) => {
    setChannel(newChannel);
    const triggers = CHANNEL_TRIGGERS[newChannel] ?? [];
    let nextTrigger = triggerType;
    if (triggers.length === 1) {
      nextTrigger = triggers[0];
    } else if (!triggers.includes(triggerType)) {
      nextTrigger = triggers[0] ?? "keyword";
      if (selectedTemplate) {
        toast.info("O gatilho foi ajustado para combinar com o canal selecionado.");
        setSelectedTemplate(null);
      }
    }
    if (nextTrigger !== triggerType) {
      setTriggerType(nextTrigger);
      setTriggerKeywords("");
    }
    if (newChannel === "crm") {
      setAiControlMode("automation_only");
      setMoveLeadEnabled(false);
    }
  }, [triggerType, selectedTemplate]);

  const resetForm = () => {
    setStep(0);
    setName("");
    setChannel("");
    setTriggerType("keyword");
    setTriggerKeywords("");
    setTriggerMediaType("image");
    setResponseMessage("");
    setAiControlMode("automation_then_ai");
    setSelectedTemplate(null);
    setMoveLeadEnabled(false);
    setDestinationFunnelId("");
    setDestinationFunnelName("");
    setDestinationStageId("");
    setDestinationStageName("");
    setDestinationStageSlug("");
    setCreateAsCampaign(false);
    setUtmSource("");
    setUtmMedium("");
    setUtmCampaign("");
    setAdSetName("");
    setAdName("");
    setCampaignNotes("");
    setCrmActions(emptyCrmWizardActions);
    setTriggerFunnelId("");
    setTriggerFunnelName("");
    setTriggerStageId("");
    setTriggerStageName("");
    setTriggerStageSlug("");
    setTriggerTagId("");
    setTriggerTagName("");
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const applyTemplate = (templateId: string) => {
    const template = TEMPLATES.find(t => t.id === templateId);
    if (!template) return;

    if (template.channel) {
      handleChannelChange(template.channel);
    } else if (channel && !CHANNEL_TRIGGERS[channel]?.includes(template.triggerType)) {
      toast.error("Este template não é compatível com o canal já selecionado.");
      return;
    }

    setSelectedTemplate(templateId);
    setTriggerType(template.triggerType);

    if (templateId === "welcome") {
      setName("Boas-vindas");
      setResponseMessage("Olá! 👋 Seja bem-vindo(a)! Como posso ajudá-lo(a) hoje?");
    } else if (templateId === "story_engage") {
      setName("Engajamento de Story");
      setResponseMessage("Obrigado por interagir com nosso Story! 🌟 Como posso ajudá-lo(a)?");
    } else if (templateId === "qualification") {
      setName("Qualificação");
      setTriggerKeywords("preço, valor, orçamento, interesse");
      setResponseMessage("Obrigado pelo seu interesse! Vou te ajudar. Qual produto ou serviço te interessa?");
    }
  };

  /* ─── Filter templates by selected channel compatibility ─── */
  const availableTemplates = useMemo(() => {
    if (!channel) return TEMPLATES;
    return TEMPLATES.filter(t => {
      if (!t.channel) {
        // Generic template: only show if its trigger is valid for the channel
        const triggers = CHANNEL_TRIGGERS[channel] ?? [];
        return triggers.includes(t.triggerType);
      }
      return t.channel === channel;
    });
  }, [channel]);

  /* ─── Filter triggers by selected channel ─── */
  const availableTriggers = useMemo(() => {
    if (!channel) return TRIGGER_TYPES;
    const valid = CHANNEL_TRIGGERS[channel] ?? [];
    return TRIGGER_TYPES.filter(t => valid.includes(t.value));
  }, [channel]);

  const keywordTriggersNeedWords = ["keyword", "story_reply", "comment"] as const;

  const canAdvance = () => {
    switch (step) {
      case 0: return name.trim().length > 0 && channel.length > 0;
      case 1: {
        if (!triggerType.length) return false;
        if (keywordTriggersNeedWords.includes(triggerType as typeof keywordTriggersNeedWords[number])) {
          return triggerKeywords.split(",").map((k) => k.trim()).filter(Boolean).length > 0;
        }
        if (triggerType === "media_type") return triggerMediaType.length > 0;
        if (triggerType === "lead_stage_entered") {
          return triggerFunnelId.length > 0 && triggerStageId.length > 0;
        }
        if (triggerType === "tag_added") return triggerTagId.length > 0;
        return true;
      }
      case 2: return isCrmChannel ? crmActionsReady(crmActions) : responseMessage.trim().length > 0;
      case 3: return aiControlMode.length > 0;
      case 4: return !moveLeadEnabled || (destinationFunnelId.length > 0 && destinationStageId.length > 0);
      case 5: return true;
      default: return false;
    }
  };

  /* ─── Navigation ─── */
  const goNext = () => {
    const idx = LOGICAL_STEPS.indexOf(step);
    if (idx < LOGICAL_STEPS.length - 1) {
      setStep(LOGICAL_STEPS[idx + 1]);
    }
  };

  const goBack = () => {
    const idx = LOGICAL_STEPS.indexOf(step);
    if (idx > 0) {
      setStep(LOGICAL_STEPS[idx - 1]);
    }
  };

  const isLastStep = currentVisualIndex === LOGICAL_STEPS.length - 1;

  const handleSave = async () => {
    setSaving(true);
    try {
      const triggerConfig = buildTriggerConfig(triggerType, {
        keywords: triggerKeywords.split(",").map((k) => k.trim()).filter(Boolean),
        media_type: triggerMediaType,
        funnel_id: triggerFunnelId,
        funnel_name: triggerFunnelName,
        stage: triggerStageSlug,
        stage_slug: triggerStageSlug,
        stage_id: triggerStageId,
        stage_name: triggerStageName,
        tag_id: triggerTagId,
        tag_name: triggerTagName,
      });

      const { data: automation, error: automationError } = await supabase
        .from("automations")
        .insert({
          workspace_id: workspaceId,
          name: name.trim(),
          channel,
          status: "draft",
          trigger_type: triggerType,
          trigger_config: triggerConfig,
          ai_control_mode: aiControlMode,
          max_executions_per_contact: maxExecutionsForTrigger(triggerType),
          cooldown_seconds: 0,
        } as any)
        .select("id")
        .single();

      if (automationError) throw automationError;

      const automationId = (automation as any).id;

      // Build the node list dynamically based on whether destination is set
      type NodeDef = {
        node_type: string;
        config: Record<string, unknown>;
        label: string;
      };

      const nodeDefs: NodeDef[] = [
        {
          node_type: "trigger",
          config: { trigger_type: triggerType, ...triggerConfig },
          label: "Gatilho",
        },
        ...(isCrmChannel
          ? crmActionNodeDefs(crmActions)
          : [{
            node_type: "send_message",
            config: { message: responseMessage },
            label: "Enviar mensagem",
          }]),
      ];

      if (!isCrmChannel && moveLeadEnabled && destinationFunnelId && destinationStageId) {
        // Always insert move_funnel first to ensure funnel context is correct
        nodeDefs.push({
          node_type: "move_funnel",
          config: {
            funnel_id: destinationFunnelId,
            funnel_name: destinationFunnelName,
          },
          label: `Mover para funil: ${destinationFunnelName}`,
        });
        // Then move_column — engine reads `stage` (slug) on UPDATE
        nodeDefs.push({
          node_type: "move_column",
          config: {
            stage: destinationStageSlug,
            stage_id: destinationStageId,
            stage_name: destinationStageName,
            funnel_id: destinationFunnelId,
          },
          label: `Mover para etapa: ${destinationStageName}`,
        });
      }

      nodeDefs.push({
        node_type: "end",
        config: {},
        label: "Fim",
      });

      const baseY = 50;
      const stepY = 150;
      const nodesPayload = nodeDefs.map((n, i) => ({
        automation_id: automationId,
        workspace_id: workspaceId,
        node_type: n.node_type,
        position_x: 250,
        position_y: baseY + i * stepY,
        config: n.config,
        label: n.label,
      }));

      const { data: nodes, error: nodesError } = await supabase
        .from("automation_nodes")
        .insert(nodesPayload as any)
        .select("id");

      if (nodesError) throw nodesError;

      const nodeIds = (nodes as any[]).map(n => n.id);

      // Build sequential edges: 0->1, 1->2, ..., n-2 -> n-1
      if (nodeIds.length >= 2) {
        const edgesPayload = [];
        for (let i = 0; i < nodeIds.length - 1; i++) {
          edgesPayload.push({
            automation_id: automationId,
            workspace_id: workspaceId,
            source_node_id: nodeIds[i],
            target_node_id: nodeIds[i + 1],
            branch: "default",
          });
        }
        await supabase.from("automation_edges").insert(edgesPayload as any);
      }

      // Populate compiled_flow so the engine picks up the automation immediately.
      // Engine expects { nodes: CompiledNode[], edges: CompiledEdge[] } where
      // CompiledNode = { id, type, config, label } and CompiledEdge = { source, target, branch }.
      const compiledNodes = nodeDefs.map((n, i) => ({
        id: nodeIds[i],
        type: n.node_type,
        config: n.config,
        label: n.label,
      }));
      const compiledEdges: Array<{ source: string; target: string; branch: string }> = [];
      for (let i = 0; i < nodeIds.length - 1; i++) {
        compiledEdges.push({
          source: nodeIds[i],
          target: nodeIds[i + 1],
          branch: "default",
        });
      }

      const { error: compileError } = await supabase
        .from("automations")
        .update({
          compiled_flow: { nodes: compiledNodes, edges: compiledEdges },
        } as any)
        .eq("id", automationId);

      if (compileError) {
        console.error("Failed to set compiled_flow:", compileError);
        toast.error("Automação criada, mas falhou ao compilar o fluxo. Abra no editor e publique novamente.");
        handleClose();
        onCreated();
        return;
      }

      const keywords = parsedKeywords;
      if (
        createAsCampaign &&
        canRegisterAsCampaign(channel, triggerType, keywords)
      ) {
        const { error: campaignError } = await (supabase as any)
          .from("automation_campaigns")
          .insert({
            workspace_id: workspaceId,
            automation_id: automationId,
            name: name.trim(),
            channel,
            keywords,
            status: "draft",
            utm_source: utmSource.trim() || null,
            utm_medium: utmMedium.trim() || null,
            utm_campaign: utmCampaign.trim() || null,
            ad_set_name: adSetName.trim() || null,
            ad_name: adName.trim() || null,
            notes: campaignNotes.trim() || null,
          });

        if (campaignError) {
          console.error("Campaign insert error:", campaignError);
          toast.error("Automação criada, mas falhou ao registrar a campanha.");
        }
      }

      toast.success(
        createAsCampaign && canRegisterAsCampaign(channel, triggerType, keywords)
          ? "Automação e campanha criadas como rascunho. Publique para ativar."
          : "Automação criada como rascunho. Abra no editor e publique para ativar.",
      );
      handleClose();
      onCreated();
    } catch (err: any) {
      console.error("Error creating automation:", err);
      toast.error(err.message || "Erro ao criar automação");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" />
            Nova Automação
          </DialogTitle>
          {/* Step indicator — adapts to number of logical steps */}
          <div className="flex items-center gap-1.5 pt-2">
            {Array.from({ length: totalVisualSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  i <= currentVisualIndex ? "bg-primary" : "bg-muted"
                }`}
              />
            ))}
          </div>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Step 0: Name + Channel + Templates */}
          {step === 0 && (
            <>
              {availableTemplates.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground font-medium">Templates rápidos</Label>
                  <div className="grid gap-2">
                    {availableTemplates.map(t => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => applyTemplate(t.id)}
                        className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${
                          selectedTemplate === t.id
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50 hover:bg-muted/50"
                        }`}
                      >
                        <Bot className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium">{t.label}</p>
                          <p className="text-xs text-muted-foreground">{t.description}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">Nome da automação</Label>
                <Input
                  id="name"
                  placeholder="Ex: Boas-vindas WhatsApp"
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Canal</Label>
                <div className="grid grid-cols-2 gap-2">
                  {CHANNELS.map(ch => {
                    const Icon = ch.icon;
                    return (
                      <button
                        key={ch.value}
                        type="button"
                        onClick={() => handleChannelChange(ch.value)}
                        className={`flex items-center gap-2 p-3 rounded-lg border text-left text-sm transition-colors ${
                          channel === ch.value
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50"
                        }`}
                      >
                        <Icon className={`h-4 w-4 ${ch.color}`} />
                        {ch.label}
                      </button>
                    );
                  })}
                </div>
                {channelHasFixedTrigger && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Gatilho definido automaticamente: <Badge variant="secondary" className="text-xs ml-1">{TRIGGER_TYPES.find(t => t.value === triggerType)?.label}</Badge>
                  </p>
                )}
              </div>
            </>
          )}

          {/* Step 1: Trigger (only for channels with multiple triggers) */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Tipo de gatilho</Label>
                <RadioGroup value={triggerType} onValueChange={setTriggerType}>
                  {availableTriggers.map(t => (
                    <div key={t.value} className="flex items-start gap-3 p-3 rounded-lg border">
                      <RadioGroupItem value={t.value} id={`trigger-${t.value}`} className="mt-0.5" />
                      <label htmlFor={`trigger-${t.value}`} className="cursor-pointer">
                        <p className="text-sm font-medium">{t.label}</p>
                        <p className="text-xs text-muted-foreground">{t.description}</p>
                      </label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {triggerType === "media_type" && (
                <div className="space-y-2">
                  <Label>Tipo de mídia que dispara a automação</Label>
                  <Select value={triggerMediaType} onValueChange={setTriggerMediaType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="image">Imagem</SelectItem>
                      <SelectItem value="audio">Áudio</SelectItem>
                      <SelectItem value="video">Vídeo</SelectItem>
                      <SelectItem value="document">Documento</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Corresponde ao tipo detectado no WhatsApp, Instagram DM ou Messenger.
                  </p>
                </div>
              )}

              {triggerType === "lead_stage_entered" && (
                <AutomationFunnelStageSelector
                  workspaceId={workspaceId}
                  selectedFunnelId={triggerFunnelId}
                  selectedFunnelName={triggerFunnelName}
                  selectedStageId={triggerStageId}
                  selectedStageName={triggerStageName}
                  onSelect={(data) => {
                    setTriggerFunnelId(data.funnel_id);
                    setTriggerFunnelName(data.funnel_name);
                    setTriggerStageId(data.stage_id);
                    setTriggerStageName(data.stage_name);
                    setTriggerStageSlug(data.stage_slug);
                  }}
                />
              )}

              {triggerType === "tag_added" && (
                <div className="space-y-2">
                  <Label>Tag que dispara a automação</Label>
                  <AutomationTagSelector
                    workspaceId={workspaceId}
                    selectedTagId={triggerTagId || undefined}
                    selectedTagName={triggerTagName || undefined}
                    onSelect={(tag) => {
                      setTriggerTagId(tag.id);
                      setTriggerTagName(tag.name);
                    }}
                  />
                </div>
              )}

              {(triggerType === "keyword" || triggerType === "story_reply" || triggerType === "comment") && (
                <div className="space-y-2">
                  <Label htmlFor="keywords">Palavras-chave (separadas por vírgula)</Label>
                  <Input
                    id="keywords"
                    placeholder="preço, valor, orçamento"
                    value={triggerKeywords}
                    onChange={e => setTriggerKeywords(e.target.value)}
                  />
                  {triggerKeywords && (
                    <div className="flex flex-wrap gap-1">
                      {triggerKeywords.split(",").map(k => k.trim()).filter(Boolean).map((k, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{k}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {showCampaignOption && (
                <div className="rounded-lg border p-4 space-y-3 mt-2">
                  <div className="flex items-start gap-3">
                    <Megaphone className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between gap-3">
                        <Label htmlFor="create-campaign" className="cursor-pointer text-sm font-medium">
                          Registrar como campanha
                        </Label>
                        <Switch
                          id="create-campaign"
                          checked={createAsCampaign}
                          onCheckedChange={setCreateAsCampaign}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Rastreie métricas na aba Analytics → Campanhas (disparos, match, contatos, handoff IA).
                      </p>
                    </div>
                  </div>

                  {createAsCampaign && (
                    <div className="space-y-3 pt-2 border-t">
                      {channel === "whatsapp" && (
                        <>
                          <p className="text-xs font-medium text-muted-foreground">
                            Rastreamento de anúncios (Meta / WhatsApp)
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">UTM Source</Label>
                              <Input
                                value={utmSource}
                                onChange={e => setUtmSource(e.target.value)}
                                placeholder="facebook"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">UTM Medium</Label>
                              <Input
                                value={utmMedium}
                                onChange={e => setUtmMedium(e.target.value)}
                                placeholder="cpc"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">UTM Campaign</Label>
                            <Input
                              value={utmCampaign}
                              onChange={e => setUtmCampaign(e.target.value)}
                              placeholder="black-friday-2026"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Conjunto de anúncios</Label>
                            <Input
                              value={adSetName}
                              onChange={e => setAdSetName(e.target.value)}
                              placeholder="Público quente"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Nome do anúncio</Label>
                            <Input
                              value={adName}
                              onChange={e => setAdName(e.target.value)}
                              placeholder="Criativo vídeo A"
                            />
                          </div>
                        </>
                      )}
                      {channel !== "whatsapp" && (
                        <>
                          <div>
                            <Label className="text-xs">Nome da campanha (ads)</Label>
                            <Input
                              value={utmCampaign}
                              onChange={e => setUtmCampaign(e.target.value)}
                              placeholder="promo-instagram-marco"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Nome do anúncio</Label>
                            <Input
                              value={adName}
                              onChange={e => setAdName(e.target.value)}
                              placeholder="Story promoção"
                            />
                          </div>
                        </>
                      )}
                      <div>
                        <Label className="text-xs">Observações</Label>
                        <Textarea
                          value={campaignNotes}
                          onChange={e => setCampaignNotes(e.target.value)}
                          placeholder="Ex: link do criativo, público-alvo..."
                          rows={2}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Response or CRM actions */}
          {step === 2 && isCrmChannel && (
            <CrmWizardFields workspaceId={workspaceId} value={crmActions} onChange={setCrmActions} />
          )}
          {step === 2 && !isCrmChannel && (
            <div className="space-y-2">
              <Label htmlFor="response">Mensagem de resposta</Label>
              <Textarea
                id="response"
                placeholder="Digite a mensagem que será enviada automaticamente..."
                value={responseMessage}
                onChange={e => setResponseMessage(e.target.value)}
                rows={5}
              />
              <p className="text-xs text-muted-foreground">
                {responseMessage.length}/10000 caracteres
              </p>
            </div>
          )}

          {/* Step 3: AI Control */}
          {step === 3 && (
            <div className="space-y-2">
              <Label>Controle de IA</Label>
              <RadioGroup value={aiControlMode} onValueChange={setAiControlMode}>
                {AI_CONTROL_MODES.map(m => (
                  <div key={m.value} className="flex items-start gap-3 p-3 rounded-lg border">
                    <RadioGroupItem value={m.value} id={`ai-${m.value}`} className="mt-0.5" />
                    <label htmlFor={`ai-${m.value}`} className="cursor-pointer">
                      <p className="text-sm font-medium">{m.label}</p>
                      <p className="text-xs text-muted-foreground">{m.description}</p>
                    </label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {/* Step 4: Lead Destination (optional) */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 rounded-lg border">
                <Target className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1 space-y-1">
                  <div className="flex items-center justify-between gap-3">
                    <Label htmlFor="move-lead-toggle" className="cursor-pointer text-sm font-medium">
                      Mover lead para outro funil/etapa
                    </Label>
                    <Switch
                      id="move-lead-toggle"
                      checked={moveLeadEnabled}
                      onCheckedChange={setMoveLeadEnabled}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Quando esta automação rodar, o lead será movido automaticamente para o funil e etapa escolhidos.
                  </p>
                </div>
              </div>

              {moveLeadEnabled && (
                <AutomationFunnelStageSelector
                  workspaceId={workspaceId}
                  selectedFunnelId={destinationFunnelId}
                  selectedFunnelName={destinationFunnelName}
                  selectedStageId={destinationStageId}
                  selectedStageName={destinationStageName}
                  onSelect={(data) => {
                    setDestinationFunnelId(data.funnel_id);
                    setDestinationFunnelName(data.funnel_name);
                    setDestinationStageId(data.stage_id);
                    setDestinationStageName(data.stage_name);
                    setDestinationStageSlug(data.stage_slug);
                  }}
                />
              )}

              {!moveLeadEnabled && (
                <p className="text-xs text-muted-foreground px-1">
                  Sem movimentação: o lead permanecerá no funil onde foi criado pela integração de origem.
                </p>
              )}
            </div>
          )}

          {/* Step 5: Review */}
          {step === 5 && (
            <div className="space-y-3">
              <h3 className="font-medium text-sm">Revisão</h3>
              <div className="rounded-lg border p-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Nome</span>
                  <span className="font-medium">{name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Canal</span>
                  <Badge variant="outline">
                    {CHANNELS.find(c => c.value === channel)?.label || channel}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Gatilho</span>
                  <span>{TRIGGER_TYPES.find(t => t.value === triggerType)?.label}</span>
                </div>
                {triggerType === "keyword" && triggerKeywords && (
                  <div className="flex justify-between items-start">
                    <span className="text-muted-foreground">Palavras-chave</span>
                    <div className="flex flex-wrap gap-1 justify-end max-w-[200px]">
                      {triggerKeywords.split(",").map(k => k.trim()).filter(Boolean).map((k, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{k}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {!isCrmChannel && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Controle IA</span>
                    <span>{AI_CONTROL_MODES.find(m => m.value === aiControlMode)?.label}</span>
                  </div>
                )}
                {triggerType === "lead_stage_entered" && triggerStageName && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Coluna</span>
                    <span className="text-right">{triggerFunnelName} → {triggerStageName}</span>
                  </div>
                )}
                {triggerType === "tag_added" && triggerTagName && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tag</span>
                    <span>{triggerTagName}</span>
                  </div>
                )}
                {!isCrmChannel && (
                  <div className="flex justify-between items-start">
                    <span className="text-muted-foreground">Destino do lead</span>
                    <span className="text-right max-w-[220px]">
                      {moveLeadEnabled && destinationFunnelName && destinationStageName ? (
                        <Badge variant="secondary" className="text-xs">
                          {destinationFunnelName} → {destinationStageName}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">Mantém no funil de origem</span>
                      )}
                    </span>
                  </div>
                )}
                {createAsCampaign && showCampaignOption && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Campanha</span>
                    <Badge variant="secondary" className="text-xs">Sim — Analytics → Campanhas</Badge>
                  </div>
                )}
                {!isCrmChannel && (
                  <div className="pt-2 border-t">
                    <span className="text-muted-foreground text-xs">Mensagem</span>
                    <p className="mt-1 text-sm bg-muted/50 rounded p-2 whitespace-pre-wrap">{responseMessage}</p>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                A automação será salva como <Badge variant="secondary" className="text-xs">Rascunho</Badge>.
                Revise o fluxo no editor visual e use <strong>Publicar</strong> para ativar.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <div>
            {currentVisualIndex > 0 && (
              <Button variant="ghost" onClick={goBack}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose}>Cancelar</Button>
            {!isLastStep ? (
              <Button onClick={goNext} disabled={!canAdvance()}>
                Próximo <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSave} disabled={saving || !canAdvance()}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                Criar automação
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
